/**
 * RestoreCreditDialog — Phase C1 (closes A4b cockpit gap).
 *
 * Used when a boarding stay ended early (axiom-1 ramification): the
 * original N-night redemption stays in the audit log, but unconsumed
 * nights get a compensating 'restore' row that decrements sessions_used.
 *
 * Outside the 24h Undo window the merchant uses THIS dialog instead of
 * voiding the original.
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'

import { billing, type PackageRedemption } from '../api/client'
import { Button } from '../components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  original: PackageRedemption | null
  onRestored: () => void
}

export function RestoreCreditDialog({
  open,
  onOpenChange,
  original,
  onRestored,
}: Props) {
  const { t } = useTranslation()
  const [sessions, setSessions] = useState<number>(0)
  const [reason, setReason] = useState<string>('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open && original) {
      setSessions(Math.max(1, Math.floor((original.sessions_used as number) / 2)))
      setReason('')
      setError(null)
    }
  }, [open, original])

  async function submit() {
    if (!original) return
    if (sessions < 1) {
      setError('sessions_must_be_positive')
      return
    }
    if (sessions > original.sessions_used) {
      setError('sessions_exceed_consumed')
      return
    }
    if (!reason.trim()) {
      setError('reason_required')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      await billing.restoreCredit({
        original_redemption_id: original.id,
        sessions_to_restore: sessions,
        reason: reason.trim(),
      })
      onRestored()
    } catch (e) {
      const detail = (e as { detail?: string })?.detail
      const message = (e as { message?: string })?.message
      setError(detail ?? message ?? 'restore_failed')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('packages.restore.title')}</DialogTitle>
          <DialogDescription>
            {t('packages.restore.subtitle')}
          </DialogDescription>
        </DialogHeader>

        {original && (
          <div className="space-y-3">
            <div className="rounded border bg-muted/30 p-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  {t('packages.restore.original')}
                </span>
                <span className="font-mono">
                  {original.sessions_used}{' '}
                  {t('packages.restore.sessionsUsedSuffix')}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  {t('packages.restore.serviceType')}
                </span>
                <span>
                  {t(`packages.serviceType.${original.snapshot_service_type}`)}
                </span>
              </div>
            </div>

            <div>
              <Label htmlFor="restore-sessions">
                {t('packages.restore.sessionsToRestore')}
              </Label>
              <Input
                id="restore-sessions"
                type="number"
                min={1}
                max={original.sessions_used}
                value={sessions}
                onChange={(e) => setSessions(Number(e.target.value) || 0)}
                data-testid="restore-sessions"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                {t('packages.restore.sessionsHelp', {
                  max: original.sessions_used,
                })}
              </p>
            </div>

            <div>
              <Label htmlFor="restore-reason">
                {t('packages.restore.reasonLabel')}
              </Label>
              <Input
                id="restore-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t('packages.restore.reasonPlaceholder') as string}
                data-testid="restore-reason"
              />
            </div>

            {error && (
              <p className="text-sm text-destructive" data-testid="restore-error">
                {t(`packages.restore.errors.${error}`, error)}
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            {t('packages.restore.cancel')}
          </Button>
          <Button
            onClick={submit}
            disabled={submitting}
            data-testid="restore-submit"
          >
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('packages.restore.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
